import { cva, type VariantProps } from 'class-variance-authority';

export const buttonVariants = cva(
  'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all active:scale-97 data-[disabled]:opacity-60 data-[disabled]:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0 shrink-0 outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      type: {
        'default': 'border border-arena-gray-500 bg-white text-arena-gray-500 hover:bg-arena-gray-100 data-[disabled]:!bg-gray-100 data-[disabled]:!text-gray-400 data-[disabled]:!border-gray-300',

        /* ========================================
         * TEXT/LINK VARIANTS (Generic)
         * ======================================== */
        'text': 'bg-transparent text-arena-gray-500 hover:bg-gray-100',
        'ghost': 'bg-transparent text-arena-gray-500 hover:bg-gray-100',
        'link': 'bg-transparent text-arena-orange underline-offset-4 hover:underline',

        /* ========================================
         * PRIMARY VARIANTS (Arena Orange)
         * ======================================== */
        'primary': 'bg-arena-orange text-white shadow-sm border border-arena-orange hover:bg-white hover:text-arena-orange data-[disabled]:!bg-arena-orange data-[disabled]:!text-white data-[disabled]:!border-arena-orange',
        'primary-filled': 'bg-arena-orange text-white shadow-sm border border-arena-orange hover:bg-white hover:text-arena-orange data-[disabled]:!bg-arena-orange data-[disabled]:!text-white data-[disabled]:!border-arena-orange',
        'primary-outlined': 'border border-arena-orange bg-white text-arena-orange hover:bg-arena-orange hover:text-white',
        'primary-ghost': 'bg-transparent text-arena-orange hover:bg-arena-orange-100',

        /* ========================================
         * SECONDARY VARIANTS (Arena Blue)
         * ======================================== */
        'secondary': 'bg-arena-blue text-white shadow-sm border border-arena-blue hover:bg-white hover:text-arena-blue',
        'secondary-filled': 'bg-arena-blue text-white shadow-sm border border-arena-blue hover:bg-white hover:text-arena-blue',
        'secondary-outlined': 'border border-arena-blue bg-white text-arena-blue hover:bg-arena-blue hover:text-white',
        'secondary-ghost': 'bg-transparent text-arena-blue hover:bg-arena-blue-100',

        /* ========================================
         * CONTRAST VARIANTS (Gray)
         * ======================================== */
        'contrast': 'bg-arena-gray-100 text-arena-gray-800 border border-arena-gray-100 hover:bg-arena-gray-200',
        'contrast-filled': 'bg-arena-gray-100 text-arena-gray-500 border border-arena-gray-100 hover:bg-arena-gray-200',
        'contrast-outlined': 'border border-arena-gray-300 bg-white text-arena-gray-800 hover:bg-arena-gray-100',
        'contrast-ghost': 'bg-transparent text-arena-gray-800 hover:bg-gray-100',

        /* ========================================
         * DANGER VARIANTS (Arena Red)
         * ======================================== */
        'danger': 'bg-arena-red-500 text-white shadow-sm border border-arena-red-500 hover:bg-white hover:text-arena-red-500',
        'danger-filled': 'bg-arena-red-500 text-white shadow-sm border border-arena-red-500 hover:bg-white hover:text-arena-red-500',
        'danger-outlined': 'border border-arena-red-500 bg-white text-arena-red-500 hover:bg-arena-red-500 hover:text-white',
        'danger-ghost': 'bg-transparent text-arena-red-500 hover:bg-arena-red-100',

        /* ========================================
         * SUCCESS VARIANTS (Arena Green)
         * ======================================== */
        'success': 'bg-arena-green-500 text-white shadow-sm border border-arena-green-500 hover:bg-white hover:text-arena-green-500',
        'success-filled': 'bg-arena-green-500 text-white shadow-sm border border-arena-green-500 hover:bg-white hover:text-arena-green-500',
        'success-outlined': 'border border-arena-green-500 bg-white text-arena-green-500 hover:bg-arena-green-500 hover:text-white',
        'success-ghost': 'bg-transparent text-arena-green-500 hover:bg-arena-green-100',

        /* ========================================
         * THEME VARIANTS (Gradient Theme)
         * ======================================== */
        'theme': 'theme-bg text-white shadow-sm hover:opacity-90',
        'theme-filled': 'theme-bg text-white shadow-sm hover:opacity-90',
        'theme-outlined': 'border theme-border bg-white [&>span]:theme-text [&>svg]:theme-text [&_i]:theme-text',
        'theme-ghost': 'bg-transparent theme-text hover:bg-gradient-to-r hover:from-arena-orange-50 hover:to-arena-blue-50',
      },
      size: {
        xxs: 'h-6 px-2 py-1 data-[icon-only]:size-6 data-[icon-only]:p-0',
        xs: 'h-7 px-3 py-1.5 data-[icon-only]:size-7 data-[icon-only]:p-0',
        sm: 'h-8 rounded-md gap-1.5 px-3 data-[icon-only]:size-8 data-[icon-only]:p-0',
        default: 'h-9 px-4 py-2 data-[icon-only]:size-9 data-[icon-only]:p-0',
        lg: 'h-10 rounded-md px-6 data-[icon-only]:size-10 data-[icon-only]:p-0',
        xl: 'h-11 rounded-md px-8 data-[icon-only]:size-11 data-[icon-only]:p-0',
        xxl: 'h-12 rounded-md px-10 data-[icon-only]:size-12 data-[icon-only]:p-0',
      },
      shape: {
        default: 'rounded-md',
        circle: 'rounded-full',
        square: 'rounded-none',
      },
      full: {
        true: 'w-full',
      },
      loading: {
        true: 'opacity-50 pointer-events-none',
      },
    },
    defaultVariants: {
      type: 'default',
      size: 'default',
      shape: 'default',
    },
  },
);

/** All valid keys accepted by the `type` variant of `buttonVariants`. */
export type ButtonVariantType = NonNullable<VariantProps<typeof buttonVariants>['type']>;
